export type HubCounterDiagnostic = {
  label: string;
  value: string;
  colorScheme: string;
};

export type HubCounterDiagnosticsInput = {
  lifecycle?: string | null;
  displayState?: string | null;
  lastFeedStatus?: string | null;
  createFeedCompleted?: boolean | null;
  sessionStatus?: string | null;
};

export type HubCounterManualStatus = 'DRAFT' | 'PENDING' | 'WAITLIST' | 'CONFIRMED' | 'REJECTED';

export type HubCounterDiagnostics = {
  lifecycle: HubCounterDiagnostic;
  displayState: HubCounterDiagnostic;
  feed: HubCounterDiagnostic;
  session: HubCounterDiagnostic;
  statusTargets: HubCounterManualStatus[];
  /** PENDING or WAITLIST → send Brevo 218 */
  canSendWaitlistEmail: boolean;
};

const lifecycleColor: Record<string, string> = {
  DRAFT: 'gray',
  PENDING: 'yellow',
  WAITLIST: 'orange',
  CONFIRMED: 'blue',
  ACTIVE: 'green',
  INACTIVE: 'red',
  REJECTED: 'red',
  RETRY: 'orange',
};

const displayStateColor: Record<string, string> = {
  PENDING: 'yellow',
  WAITLIST: 'orange',
  LOGIN_REQUIRED: 'yellow',
  CONFIRMED: 'green',
  REJECTED: 'red',
};

const feedColor: Record<string, string> = {
  PENDING: 'yellow',
  SUBMITTED: 'blue',
  IN_PROGRESS: 'yellow',
  COMPLETED: 'green',
  COMPLETED_WITH_WARNINGS: 'orange',
  CANCELLED: 'gray',
  FAILED: 'red',
};

const sessionColor: Record<string, string> = {
  ACTIVE: 'green',
  INVALID: 'red',
  EXPIRED: 'orange',
  MISSING: 'gray',
};

const MANUAL_HUB_COUNTER_STATUS_TARGETS: Record<string, readonly HubCounterManualStatus[]> = {
  PENDING: ['WAITLIST', 'CONFIRMED', 'REJECTED'],
  WAITLIST: ['PENDING', 'CONFIRMED', 'REJECTED'],
  CONFIRMED: ['WAITLIST', 'REJECTED'],
  REJECTED: ['PENDING', 'DRAFT'],
};

function fallbackValue(value?: string | null, emptyValue = 'MISSING'): string {
  return value && value.trim().length > 0 ? value : emptyValue;
}

/**
 * Keep Endor Display aligned with backend deriveHubCounterDisplayState:
 * DRAFT / PENDING / RETRY must show PENDING (identification still in review).
 * WAITLIST is only after Endor accepts identification.
 */
function resolveDisplayState(lifecycle: string, displayState?: string | null): string {
  if (lifecycle === 'DRAFT' || lifecycle === 'PENDING' || lifecycle === 'RETRY') {
    return 'PENDING';
  }
  return fallbackValue(displayState, 'UNKNOWN');
}

export function getManualHubCounterStatusTargets(status?: string | null): HubCounterManualStatus[] {
  return [...(MANUAL_HUB_COUNTER_STATUS_TARGETS[status || ''] || [])];
}

export function resolveHubCounterDiagnostics(input: HubCounterDiagnosticsInput): HubCounterDiagnostics {
  const lifecycle = fallbackValue(input.lifecycle, 'UNKNOWN');
  const displayState = resolveDisplayState(lifecycle, input.displayState);
  const feed = input.lastFeedStatus?.trim() || (input.createFeedCompleted ? 'COMPLETED' : 'PENDING');
  const session = fallbackValue(input.sessionStatus, 'MISSING');

  return {
    lifecycle: {
      label: 'Internal',
      value: lifecycle,
      colorScheme: lifecycleColor[lifecycle] ?? 'gray',
    },
    displayState: {
      label: 'Display',
      value: displayState,
      colorScheme: displayStateColor[displayState] ?? 'gray',
    },
    feed: {
      label: 'Feed',
      value: feed,
      colorScheme: feedColor[feed] ?? 'gray',
    },
    session: {
      label: 'Session',
      value: session,
      colorScheme: sessionColor[session] ?? 'gray',
    },
    statusTargets: getManualHubCounterStatusTargets(lifecycle),
    canSendWaitlistEmail: lifecycle === 'PENDING' || lifecycle === 'WAITLIST',
  };
}
